/**
 * Centralized date utilities for consistent time zone handling
 * Fixes the time zone bug where content appears at wrong times
 */

/**
 * Creates a UTC timestamp for Contentful queries
 * Ensures consistent UTC comparison regardless of server time zone
 */
export function getCurrentUTCTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Parses a date string and ensures it's treated as UTC
 * Fixes issues with Date.parse() and new Date() inconsistencies
 */
export function parseUTCDate(dateString: string | Date): Date {
  if (dateString instanceof Date) {
    return dateString;
  }

  // Ensure the date is parsed as UTC
  const date = new Date(dateString);

  // If the string doesn't have timezone info, assume it's UTC
  if (
    !dateString.includes('Z') &&
    !dateString.includes('+') &&
    !dateString.includes('-', 10)
  ) {
    // This is a local time string, convert to UTC
    return new Date(`${dateString}Z`);
  }

  return date;
}

/**
 * Compares a release date with current time using UTC
 * Fixes the time zone comparison bug
 */
export function isContentReleased(
  releaseDate: string | Date | undefined,
): boolean {
  if (!releaseDate) {
    return true; // No release date means always released
  }

  const releaseDateUTC = parseUTCDate(releaseDate);

  // If the date is invalid, treat it as released (don't block content)
  if (isNaN(releaseDateUTC.getTime())) {
    return false;
  }

  const currentTimeUTC = new Date();

  // Compare UTC timestamps
  return releaseDateUTC <= currentTimeUTC;
}

/**
 * Gets the current time in UTC milliseconds
 * Consistent with Date.now() but ensures UTC handling
 */
export function getCurrentUTCTime(): number {
  return Date.now();
}

/**
 * Formats a date for Contentful queries
 * Ensures proper UTC formatting for API calls
 */
export function formatForContentfulQuery(date: Date): string {
  return date.toISOString();
}

/**
 * Enhanced date factory that handles time zones correctly
 * Replaces the simple dateFactory utility
 */
export function createUTCDate(isoString: string | Date): Date {
  return parseUTCDate(isoString);
}
